import React from "react";
import { Spinner, Skeleton } from "../ui";

type SpinnerSize = "small" | "medium" | "large";

const spinnerSizeMap: Record<SpinnerSize, "sm" | "md" | "lg"> = {
  small: "sm",
  medium: "md",
  large: "lg",
};

interface LoadingStateProps {
  /** Loading message to display */
  message?: string;
  /** Size of the spinner */
  size?: SpinnerSize;
  /** Additional CSS classes */
  className?: string;
  /** If true, renders inline without container */
  inline?: boolean;
}

/**
 * LoadingState Component
 * Displays a shadcn-style spinner with optional message
 */
export const LoadingState: React.FC<LoadingStateProps> = ({
  message = "Loading...",
  size = "medium",
  className = "",
  inline = false,
}) => {
  const spinnerSize = spinnerSizeMap[size] || "md";

  if (inline) {
    return (
      <span className={`inline-flex items-center gap-2 ${className}`}>
        <Spinner size={spinnerSize} />
        {message && <span className="text-sm text-muted-foreground">{message}</span>}
      </span>
    );
  }

  return (
    <div className={`loading-container ${className}`}>
      <Spinner size={spinnerSize} />
      {message && <p>{message}</p>}
    </div>
  );
};

interface LoadingSpinnerProps {
  /** Size of the spinner */
  size?: SpinnerSize;
  /** Additional CSS classes */
  className?: string;
}

/**
 * LoadingSpinner Component
 * Standalone shadcn-style spinner without message
 */
export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = "medium",
  className = "",
}) => {
  const spinnerSize = spinnerSizeMap[size] || "md";
  return <Spinner size={spinnerSize} className={className} />;
};

interface TableCellLoadingProps {
  /** Width of the skeleton */
  width?: string;
  /** Height of the skeleton */
  height?: string;
  /** Additional CSS classes */
  className?: string;
}

/**
 * TableCellLoading Component
 * Skeleton placeholder for table cells
 */
export const TableCellLoading: React.FC<TableCellLoadingProps> = ({
  width = "80%",
  height = "16px",
  className = "",
}) => {
  return (
    <Skeleton
      className={className}
      style={{
        width,
        height,
        display: "inline-block",
        verticalAlign: "middle",
      }}
    />
  );
};

interface ProgressLoaderProps {
  /** Number of items loaded */
  loaded?: number;
  /** Total number of items */
  total?: number;
  /** Loading message */
  message?: string;
  /** Additional CSS classes */
  className?: string;
}

/**
 * ProgressLoader Component
 * Loading state with progress indicator
 */
export const ProgressLoader: React.FC<ProgressLoaderProps> = ({
  loaded = 0,
  total = 0,
  message = "Loading...",
  className = "",
}) => {
  const percentage = total > 0 ? Math.round((loaded / total) * 100) : 0;

  return (
    <div className={`loading-progress ${className}`}>
      <div className="progress-info">
        {message} {loaded} / {total}
        <span className="progress-percentage">({percentage}%)</span>
      </div>
      <div className="progress-bar-container">
        <div
          className="progress-bar-fill"
          style={{
            width: `${percentage}%`,
            transition: `width var(--duration-deliberate, 600ms) var(--ease-decelerate, cubic-bezier(0.0, 0.0, 0.2, 1))`,
          }}
        />
      </div>
    </div>
  );
};

export default LoadingState;
